import React, { useContext } from "react"
import {
  RetentionSalesEventHeroProps,
  UrlBackgroundVariant,
} from "./RetentionSalesEventHero.d"
import tw from "twin.macro"
import { Image } from "../../atoms/Image"
import BlockContent from "../../atoms/BlockContent"
import { css } from "@emotion/react"
import { LanguageContext } from "../../../contexts/Language"
import { DisclaimersContext } from "../../../contexts/Disclaimers"
import { toggleDisclaimersModal } from "../../../contexts/Disclaimers/actions"
import { getCTALinkValue, parseDisclaimerBlocks } from "../../../helpers"
import { ButtonLink } from "../../atoms/Button"
import useTealiumEvent from "../../../hooks/Tealium/useTealiumEvent"
import { TealiumContext } from "../../../contexts/Tealium"
import { CTA } from "../../../global"

const RetentionSalesEventHero: React.FC<RetentionSalesEventHeroProps> = ({
  desktopImage,
  mobileImage,
  cta,
  saleLogo,
  saleLogoMobile,
  promoText,
  promoTextColor,
  endDateText,
  endDateTextColor,
  endDateTextColorDesktop,
  backgroundColor,
  backgroundColorDesktop,
  contentBackgroundColor,
  contentBackgroundColorDesktop,
  heroType,
  urlParamBackgroundVariants,
}) => {
  const { language } = useContext(LanguageContext)
  const [state, dispatch] = useContext(DisclaimersContext)

  let desktopBackgroundSrc = desktopImage
  let mobileBackgroundSrc = mobileImage
  let backgroundColorValue = backgroundColor
  let promoTextColorValue = promoTextColor?.hex ? { ...promoTextColor } : null
  let endDateTextColorValue = endDateTextColor?.hex
    ? { ...endDateTextColor }
    : null
  let contentBackgroundColorValue = contentBackgroundColor?.rgb
    ? { ...contentBackgroundColor }
    : null

  // For URL Param Hero type - checking for URL param and getting background variant assoiciated with URL param
  if (heroType === "urlParamHero" && urlParamBackgroundVariants?.length > 0) {
    const queryString =
      typeof window !== "undefined" ? window.location.search : ""
    const urlParams = new URLSearchParams(queryString)

    for (const [key, value] of urlParams?.entries()) {
      const selectedBackgroundVariant = urlParamBackgroundVariants?.find(
        (backgroundVariant: UrlBackgroundVariant) =>
          backgroundVariant.urlParamKey === key &&
          backgroundVariant.urlParamValue === value,
      )
      if (selectedBackgroundVariant) {
        desktopBackgroundSrc = selectedBackgroundVariant.backgroundImage
        mobileBackgroundSrc = selectedBackgroundVariant.mobileBackgroundImage
        backgroundColorValue = selectedBackgroundVariant.backgroundColor
        promoTextColorValue = selectedBackgroundVariant?.promoTextColor?.hex
          ? { ...selectedBackgroundVariant.promoTextColor }
          : null
        endDateTextColorValue = selectedBackgroundVariant?.endDateTextColor?.hex
          ? { ...selectedBackgroundVariant.endDateTextColor }
          : null
        contentBackgroundColorValue = selectedBackgroundVariant
          .contentBackgroundColor?.rgb
          ? { ...selectedBackgroundVariant.contentBackgroundColor }
          : null
      }
    }
  }

  const promoTextStyles = css`
    ${tw`text-center font-black text-[2.25rem] leading-[2.25rem] mb-0 md:(text-5xl) lg:(text-[3.5rem])`}
    h1,h2,h3,h4 {
      ${tw`text-[4rem] leading-[3.25rem] mb-0 md:(text-8xl) lg:(text-[7rem])`}
    }
    .disclaimer {
      text-xs
    }
    color: ${promoTextColorValue?.hex ? promoTextColorValue.hex : "#FFF"}
  `

  const promoTextStylesES = css`
    ${tw`text-center font-black text-[2rem] leading-[2rem] mb-0 md:(text-4xl) lg:(text-[3rem])`}
    h1,h2,h3,h4 {
      ${tw`text-[3rem] leading-[3.25rem] mb-0 md:(text-7xl) lg:(text-[6rem])`}
    }
    .disclaimer {
      text-xs
    }
    color: ${promoTextColorValue?.hex ? promoTextColorValue.hex : "#FFF"}
  `

  const desktopBackgroundColorStyles = contentBackgroundColorDesktop
    ? css`
        @media (min-width: 1024px) {
          background-color: rgba(
            ${contentBackgroundColorDesktop.rgb.r},
            ${contentBackgroundColorDesktop.rgb.g},
            ${contentBackgroundColorDesktop.rgb.b},
            ${contentBackgroundColorDesktop.rgb.a}
          );
        }
      `
    : contentBackgroundColorValue
      ? css`
          @media (min-width: 1024px) {
            background-color: rgba(
              ${contentBackgroundColorValue.rgb.r},
              ${contentBackgroundColorValue.rgb.g},
              ${contentBackgroundColorValue.rgb.b},
              ${contentBackgroundColorValue.rgb.a}
            );
          }
        `
      : null

  const mobileBackgroundColorStyles = contentBackgroundColor
    ? css`
        background-color: rgba(
          ${contentBackgroundColor.rgb.r},
          ${contentBackgroundColor.rgb.g},
          ${contentBackgroundColor.rgb.b},
          ${contentBackgroundColor.rgb.a}
        );
      `
    : null

  //tealium
  const { trackTealEvent } = useTealiumEvent()
  const { tealPageData } = useContext(TealiumContext)
  const handleClick = (cta: CTA) => {
    trackTealEvent({
      tealium_event: "cta_click",
      coupon_module_text: cta?.title,
      link_href: getCTALinkValue(cta),
    })
  }
  return (
    <section
      css={[
        tw`flex flex-col h-auto relative min-h-[680px]`,
        tw`lg:(items-center flex-row h-[680px])`,
      ]}
      aria-label="Parts & Services Hero Section"
      className="optimizelyTest11Control"
    >
      <div css={tw`relative md:(static)`}>
        {/* {LottieJson && (
          <div
            css={[
              tw`hidden relative w-full h-[380px] object-cover object-[50% 50%]`,
              `@media screen and (min-width: 390px) {
                object-position: 100% 100%;
                height: auto;
              }`,
              tw`md:(block h-[380px] object-[100% 100%])`,
              tw`lg:(h-[600px] absolute object-[66% 50%] top-0)`,
              "z-index: 0;",
            ]}
          >
            <Lottie
              loop
              animationData={LottieJson}
              play
              style={{
                width: "100%",
                height: "100%",
                // backgroundSize: 'cover',
                backgroundPosition: "center",
                objectFit: "cover",
              }}
              rendererSettings={{ preserveAspectRatio: "xMidYMid slice" }}
            />
          </div>
        )} */}
        {/* Desktop Background Image */}
        {desktopBackgroundSrc && (
          <Image
            imageData={desktopBackgroundSrc?.image}
            css={[
              tw`hidden`,
              tw`md:(block relative w-full object-cover h-[550px] object-[88% 50%])`,
              tw`lg:(h-[680px] absolute object-[66% 50%] top-0)`,
              "z-index: 0;",
            ]}
          />
        )}
        {/* Mobile Background Image */}
        <Image
          imageData={
            mobileBackgroundSrc?.image
              ? mobileBackgroundSrc.image
              : desktopBackgroundSrc?.image
          }
          css={[
            tw`relative block w-full h-auto object-cover object-[50% 50%]`,
            `@media screen and (min-width: 390px) {
              object-position: 100% 50%;
            }`,
            tw`md:(hidden)`,
            "z-index: 0;",
          ]}
        />
        {promoText && (
          <div
            css={[
              tw`flex flex-col z-20 absolute right-[30%] top-1/2 transform -translate-y-1/2 align-middle`,
              tw`sm:(right-32)`,
              tw`md:(right-[19rem] top-[17rem])`,
              tw`lg:(right-auto left-[65%] top-1/2)`,
              tw`desktop-hd:(left-[67%])`,
              language === "es" ? promoTextStylesES : promoTextStyles,
              language === "es" &&
                tw`right-[4.5rem] sm:(right-24) md:(right-[15rem] top-[18rem]) lg:(left-auto right-16 top-[21rem]) xl:(right-auto left-[59%]) desktop-hd:(left-[62%])`,
            ]}
          >
            <BlockContent data={promoText} />
          </div>
        )}
      </div>
      <div css={[tw`w-full`, tw`lg:(mx-auto z-10 max-w-[1440px])`]}>
        <div
          css={[
            tw`flex p-6 gap-4 flex-col w-full text-white`,
            tw`lg:(bg-transparent rounded-lg inline-flex w-auto)`,
            mobileBackgroundColorStyles,
            desktopBackgroundColorStyles,
          ]}
        >
          {/* Desktop Sales Event Logo */}
          {saleLogo && (
            <Image
              imageData={saleLogo?.image}
              css={[tw`hidden`, tw`lg:(block w-full max-w-[450px] mb-1)`]}
            />
          )}
          {/* Mobile Sales Event Logo */}
          {saleLogoMobile && (
            <Image
              imageData={saleLogoMobile?.image}
              css={[tw`block w-full max-w-[400px] mb-1`, tw`lg:(hidden)`]}
            />
          )}
          <hr
            css={[
              tw`border-black border-2 max-w-[400px]`,
              tw`lg:(w-full max-w-[450px])`,
            ]}
          />
          {endDateText && (
            <p
              css={[
                tw`text-2xl font-bold`,
                tw`lg:(text-3xl)`,
                `@media (max-width: 1024px) {
                color:${endDateTextColor?.hex};
              }`,
                `@media (min-width: 1024px) {
                color:${endDateTextColorDesktop?.hex};
              }`,
              ]}
            >
              {parseDisclaimerBlocks(endDateText, code =>
                dispatch(toggleDisclaimersModal(code)),
              )}
            </p>
          )}
          {cta && (
            <div
              css={[
                tw`col-span-2 col-start-1 flex mt-4 justify-center row-start-6 items-center gap-2`,
                tw`md:(col-span-1 row-start-3 row-end-4 justify-start my-0 mt-4)`,
              ]}
            >
              <ButtonLink
                {...{ [cta?.buttonType]: true }}
                to={getCTALinkValue(cta)}
                target={cta?.linkType === "external" ? "_blank" : "_self"}
                css={[
                  tw`w-full col-span-1 h-full flex justify-center items-center !text-xs !px-2.5 whitespace-nowrap`,
                  tw`md:(w-auto !text-sm !px-4)`,
                ]}
                key={cta?.title}
                onClick={() => handleClick(cta)}
                analytics-id={cta?.title ? `${cta?.title}:hero:` : null}
                className="optimizelyPartsHeroCTA"
              >
                {cta?.title}
              </ButtonLink>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default RetentionSalesEventHero
